import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { toastApiError } from "@/lib/errorMessages";
import {
  adminSettlementKeys,
  adminSettlementService,
  type ReplaceSupplierInvoiceInput,
  type RevolutPayoutMode,
  type SettlementLineRow,
  type SettlementRevolutPayoutResponse,
  type SettlementSummaryEmailResponse,
} from "@/services/adminSettlementService";
import { linesEligibleForSummaryEmail } from "../settlementUi";
import type { RefreshSettlementDetail, Submitting } from "./utils";

export type SettlementReferenceInput = { reference: string; note?: string };

export type SupplierInvoiceInput = {
  supplierInvoiceNumber: string;
  supplierInvoiceDate?: string;
  totalCents: number;
  file: File;
  note?: string;
};

export type RevolutPayoutInput = SettlementReferenceInput & {
  mode: RevolutPayoutMode;
};

type SettlementMutationVars =
  | { type: "summary"; lineIds: string[] }
  | { type: "invoice"; note?: string; lineIds: string[] }
  | ({ type: "collect"; lineIds: string[] } & SettlementReferenceInput)
  | ({ type: "supplier"; lineIds: string[] } & SupplierInvoiceInput)
  | ({ type: "replaceSupplier" } & ReplaceSupplierInvoiceInput)
  | ({ type: "pay"; lineIds: string[] } & SettlementReferenceInput)
  | ({ type: "revolut"; lineIds: string[] } & RevolutPayoutInput);

type SettlementMutationResult =
  | { type: "summary"; res: SettlementSummaryEmailResponse }
  | { type: "invoice" }
  | { type: "collect" }
  | { type: "supplier" }
  | { type: "replaceSupplier" }
  | { type: "pay" }
  | { type: "revolut"; res: SettlementRevolutPayoutResponse };

const MUTATION_ERROR_KEYS: Record<Exclude<Submitting, null>, string> = {
  summary: "settlements.detail.steps.summaryError",
  invoice: "settlements.detail.invoicedError",
  collect: "settlements.detail.steps.collectError",
  supplier: "settlements.detail.steps.supplierError",
  replaceSupplier: "settlements.detail.steps.supplierReplaceError",
  pay: "settlements.detail.paidError",
  revolut: "settlements.detail.revolut.error",
};

type UseSettlementActionsOptions = {
  userId: string;
  selected: Set<string>;
  selectedLines: SettlementLineRow[];
  payableCents: number;
  refreshDetail: RefreshSettlementDetail;
};

/**
 * Workflow actions on the selected settlement lines. Handlers backed by `mutateAsync`
 * reject when the request fails so the step keeps its form for a retry; the error toast
 * is shown here either way.
 */
export function useSettlementActions({
  userId,
  selected,
  selectedLines,
  payableCents,
  refreshDetail,
}: UseSettlementActionsOptions) {
  const { t } = useTranslation("payments");
  const queryClient = useQueryClient();

  const actionMutation = useMutation({
    mutationFn: async (vars: SettlementMutationVars): Promise<SettlementMutationResult> => {
      switch (vars.type) {
        case "summary": {
          const res = await adminSettlementService.sendSummaryEmail(userId, {
            line_ids: vars.lineIds,
          });
          return { type: "summary", res };
        }
        case "invoice": {
          await adminSettlementService.markInvoiced(userId, {
            line_ids: vars.lineIds,
            note: vars.note,
          });
          return { type: "invoice" };
        }
        case "collect": {
          await adminSettlementService.recordCollection(userId, {
            line_ids: vars.lineIds,
            reference: vars.reference,
            note: vars.note,
          });
          return { type: "collect" };
        }
        case "supplier": {
          await adminSettlementService.recordSupplierInvoice(userId, {
            line_ids: vars.lineIds,
            file: vars.file,
            supplier_invoice_number: vars.supplierInvoiceNumber,
            supplier_invoice_date: vars.supplierInvoiceDate,
            total_cents: vars.totalCents,
            note: vars.note,
          });
          return { type: "supplier" };
        }
        case "replaceSupplier": {
          await adminSettlementService.replaceSupplierInvoice(vars);
          return { type: "replaceSupplier" };
        }
        case "pay": {
          await adminSettlementService.markPaid(userId, {
            line_ids: vars.lineIds,
            reference: vars.reference,
            note: vars.note,
          });
          return { type: "pay" };
        }
        case "revolut": {
          const res = await adminSettlementService.initiateRevolutPayout(userId, {
            line_ids: vars.lineIds,
            mode: vars.mode,
            reference: vars.reference,
            note: vars.note,
          });
          return { type: "revolut", res };
        }
      }
    },
    onSuccess: async (result) => {
      switch (result.type) {
        case "summary":
          if (result.res.sent) {
            toast.success(t("settlements.detail.steps.summarySuccess"));
            await refreshDetail({ resetFilters: false });
          } else {
            toast.error(
              t("settlements.detail.steps.summarySkipped", {
                detail: result.res.detail ?? "",
              }),
            );
          }
          break;
        case "invoice":
          toast.success(t("settlements.detail.invoicedSuccess"));
          await refreshDetail({ resetFilters: true });
          break;
        case "collect":
          toast.success(t("settlements.detail.steps.collectSuccess"));
          await refreshDetail({ resetFilters: true });
          break;
        case "supplier":
          toast.success(t("settlements.detail.steps.supplierSuccess"));
          await refreshDetail({ resetFilters: true });
          break;
        case "replaceSupplier":
          toast.success(t("settlements.detail.steps.supplierReplaceSuccess"));
          await refreshDetail({ resetFilters: false });
          break;
        case "pay":
          toast.success(t("settlements.detail.paidSuccess"));
          await refreshDetail({ resetFilters: true });
          break;
        case "revolut":
          if (result.res.status === "COMPLETED") {
            toast.success(t("settlements.detail.revolut.completedToast"));
            queryClient.setQueryData(adminSettlementKeys.activeRevolutPayout(userId), {
              payout: null,
            });
            await refreshDetail({ resetFilters: true });
          } else {
            toast.success(t("settlements.detail.revolut.initiated"));
            queryClient.setQueryData(adminSettlementKeys.activeRevolutPayout(userId), {
              payout: result.res,
            });
            await refreshDetail({ resetFilters: false });
          }
          break;
      }
    },
    onError: (error, vars) => {
      toastApiError(error, "generic", t(MUTATION_ERROR_KEYS[vars.type]));
    },
  });

  const submitting: Submitting = actionMutation.isPending
    ? (actionMutation.variables?.type ?? null)
    : null;

  const sendSummary = () => {
    if (!userId || selected.size === 0) return;
    const lineIds = linesEligibleForSummaryEmail(selectedLines).map((line) => line.id);
    if (lineIds.length === 0) {
      toast.error(t("settlements.detail.steps.summaryAllInvoiced"));
      return;
    }
    actionMutation.mutate({ type: "summary", lineIds });
  };

  const markInvoiced = async (note?: string) => {
    if (!userId || selected.size === 0) return;
    await actionMutation.mutateAsync({
      type: "invoice",
      note,
      lineIds: Array.from(selected),
    });
  };

  const recordCollection = async (data: SettlementReferenceInput) => {
    if (!userId || selected.size === 0) return;
    await actionMutation.mutateAsync({
      type: "collect",
      reference: data.reference,
      note: data.note,
      lineIds: Array.from(selected),
    });
  };

  const recordSupplierInvoice = (data: SupplierInvoiceInput) => {
    if (!userId || selected.size === 0) return;
    actionMutation.mutate({
      type: "supplier",
      supplierInvoiceNumber: data.supplierInvoiceNumber,
      supplierInvoiceDate: data.supplierInvoiceDate,
      totalCents: data.totalCents,
      file: data.file,
      note: data.note,
      lineIds: Array.from(selected),
    });
  };

  const replaceSupplierInvoice = (data: ReplaceSupplierInvoiceInput) =>
    actionMutation.mutateAsync({ type: "replaceSupplier", ...data });

  const markPaid = async (data: SettlementReferenceInput) => {
    if (!userId || selected.size === 0) return;
    await actionMutation.mutateAsync({
      type: "pay",
      reference: data.reference,
      note: data.note,
      lineIds: Array.from(selected),
    });
  };

  const startRevolutPayout = async (data: RevolutPayoutInput) => {
    if (!userId || selected.size === 0) return;
    if (payableCents <= 0) return;
    await actionMutation.mutateAsync({
      type: "revolut",
      mode: data.mode,
      reference: data.reference,
      note: data.note,
      lineIds: Array.from(selected),
    });
  };

  return {
    submitting,
    sendSummary,
    markInvoiced,
    recordCollection,
    recordSupplierInvoice,
    replaceSupplierInvoice,
    markPaid,
    startRevolutPayout,
  };
}
